import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { isIElementWaypoint } from "@/hooks/use-placeholders";
import { useCurrentArea } from "@/modes/mode-data-context";
import { useNewWaypointsStore } from "@/modes/sheet-mode/sheet-context-menu/new-waypoints/new-waypoints-store";
import {
  getOffsetInCurrentTarget,
  GetWaypointInfoForDragEvent,
} from "@/modes/sheet-mode/use-waypoint-info-for-drag-event";
import { useAppSelector } from "@/store/store-hooks";
import { selectCanUploadCad } from "@/store/subscription-features/subscription-features-selectors";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  ContextualBanner,
  cyan,
  DropHandler,
  FaroText,
  grey,
  red,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import {
  assert,
  generateGUID,
  getFileExtension,
  GUID,
  isImageFileExtension,
} from "@faro-lotv/foundation";
import { selectIElement } from "@faro-lotv/project-source";
import { Stack } from "@mui/material";
import { isEqual } from "es-toolkit";
import {
  forwardRef,
  PropsWithChildren,
  DragEvent as ReactDragEvent,
  ReactNode,
  useCallback,
  useRef,
  useState,
  type JSX,
} from "react";
import { Vector2 } from "three";
import { useWaypointReference } from "../r3f/utils/placeholder-preview";
import { ImageType } from "../ui/tree/tree-context-menu/actions/upload-action";
import { isSupportedCADFileExtension } from "./point-cloud-file-upload-context/cad-upload-utils";
import { useElementFileUploadContext } from "./point-cloud-file-upload-context/element-file-upload-context";
import {
  isSupportedPCFileExtension,
  useCanImportPointCloud,
} from "./point-cloud-file-upload-context/point-cloud-upload-utils";

/** File types that we can rule out before dropping. */
const DISALLOWED_MIME_PREFIXES = [
  "text/",
  "video/",
  "audio/",
  "font/",
  // We cannot rule out all application types, as point cloud files could use these.
  // Only exclude some common file types we don't support yet.
  "application/pdf",
  "application/zip",
  "application/x-zip-compressed",
];

/** Padding of the drag tooltip to the canvas edge */
const TOOLTIP_PADDING_PX = 4;

/** Pixel offset to apply to the tooltip to prevent it from overflowing the bottom of the screen. */
const TOOLTIP_OFFSET_OVERFLOW_BOTTOM = 200;

type Upload3dDataDropHandlerProps = {
  getWaypointInfoForDragEvent?: GetWaypointInfoForDragEvent;
};

/**
 * @returns A dropzone prepared to accept only one file at a time. Files dropped here will be
 * uploaded and imported by the app.
 */
export function Upload3dDataDropHandler({
  getWaypointInfoForDragEvent,
  children,
}: PropsWithChildren<Upload3dDataDropHandlerProps>): JSX.Element {
  const { handleErrorWithDialog, handleErrorWithToast } = useErrorHandlers();

  const { setPointCloudFile, setCadFile } = useElementFileUploadContext();

  const hasWritePermission = useAppSelector(selectHasWritePermission);

  const canImportPointCloud = useCanImportPointCloud();
  const canImportCAD = useAppSelector(selectCanUploadCad);
  const canImportWaypointImages =
    !!getWaypointInfoForDragEvent && hasWritePermission;

  const { area } = useCurrentArea();

  const { addNewWaypoint } = useNewWaypointsStore();

  const { uploadMultiImageDataFiles } = useElementFileUploadContext();

  // eslint-disable-next-line func-style -- FIXME
  const onDrop = (e: ReactDragEvent<HTMLElement>): void => {
    e.preventDefault();

    const files = Array.from(e.dataTransfer.files);
    assert(files.length >= 1, "Expected at least one file");

    try {
      if (
        canImportPointCloud &&
        files.every((file) => isSupportedPCFileExtension(file.name))
      ) {
        assert(
          files.length === 1,
          "Expected exactly one file for point cloud upload",
        );
        setPointCloudFile(files[0]);
      } else if (
        canImportCAD &&
        files.every((file) => isSupportedCADFileExtension(file.name))
      ) {
        assert(files.length === 1, "Expected exactly one file for CAD upload");
        setCadFile(files[0]);
      } else if (
        canImportWaypointImages &&
        files.every((file) => isImageFileExtension(getFileExtension(file.name)))
      ) {
        assert(area, "Expected to have an area loaded when adding waypoints.");

        const waypointInfo = getWaypointInfoForDragEvent(e);

        if (!waypointInfo) return;

        if ("worldPosition" in waypointInfo) {
          addNewWaypoint({
            id: generateGUID(),
            areaId: area.id,
            type: ImageType.auto,
            name: files.length === 1 ? files[0].name : "New waypoint",
            worldPosition: waypointInfo.worldPosition,
            files,
          });
        } else {
          assert(waypointInfo.roomSection, "Room section must exist");
          uploadMultiImageDataFiles(
            files,
            waypointInfo.roomSection,
            ImageType.auto,
          );
        }
      } else {
        handleErrorWithToast({
          title: "Failed to import file",
          error: "The file extension is not supported.",
        });
      }
    } catch (error) {
      handleErrorWithDialog({ title: "Data upload", error });
    }
  };

  const getDropPreviewData = useCallback(
    (ev: ReactDragEvent<HTMLElement>): DropPreviewProps => {
      const items = Array.from(ev.dataTransfer.items);

      if (items.some((item) => item.kind !== "file")) {
        return {
          isAllowed: false,
          message: "Only files can be dropped here",
        };
      }

      if (
        items.some((item) =>
          DISALLOWED_MIME_PREFIXES.some((prefix) =>
            item.type.startsWith(prefix),
          ),
        )
      ) {
        return {
          isAllowed: false,
          message: "File type not supported for upload",
        };
      }

      const isAllImageFiles = items.every((item) =>
        item.type.startsWith("image/"),
      );

      if (!isAllImageFiles) {
        if (!canImportPointCloud && !canImportCAD) {
          return {
            isAllowed: false,
            message: "File type not supported for upload",
          };
        }

        if (items.length === 1) {
          return {
            isAllowed: true,
            message: "Drop to upload 3D data",
          };
        }
        return {
          isAllowed: false,
          message: "3D data must be uploaded one file at a time.",
        };
      }

      if (canImportWaypointImages) {
        const waypointInfo = getWaypointInfoForDragEvent(ev);

        if (!waypointInfo) {
          return {
            isAllowed: false,
            message: "Drag files into the sheet to upload.",
          };
        }

        if ("worldPosition" in waypointInfo) {
          return {
            isAllowed: true,
            message: "Drop to create a waypoint",
          };
        }

        if (!waypointInfo.roomSection) {
          return {
            isAllowed: false,
            message: "Adding images to scans is not supported",
          };
        }

        return {
          isAllowed: true,
          isHighlightedAction: true,
          snapPosition: waypointInfo.screenPosition,
          message: (
            <>
              Drop to upload images to{" "}
              <b>
                <WaypointName waypointId={waypointInfo.existingWaypoint} />
              </b>
            </>
          ),
        };
      }

      return {
        isAllowed: false,
        message: "File type not supported for upload",
      };
    },
    [
      canImportCAD,
      canImportPointCloud,
      canImportWaypointImages,
      getWaypointInfoForDragEvent,
    ],
  );

  // Only one file can be uploaded
  const canBeDropped = useCallback(
    (e: ReactDragEvent<HTMLElement>): boolean =>
      getDropPreviewData(e).isAllowed,
    [getDropPreviewData],
  );

  const [dropPreviewData, setDropPreviewData] = useState<DropPreviewProps>();

  // Using a ref here to make the tooltip UI feel more responsive and avoid frequent state updates.
  const dropPreviewRef = useRef<HTMLDivElement>(null);

  const onDragOver = useCallback(
    (e: ReactDragEvent<HTMLElement>) => {
      const preview = getDropPreviewData(e);

      const {
        left,
        top,
        width: canvasWidth,
        height: canvasHeight,
      } = getOffsetInCurrentTarget(e);
      const panelRect = dropPreviewRef.current?.getBoundingClientRect();
      const panelWidth = panelRect?.width ?? 0;
      const panelHeight = panelRect?.height ?? 0;

      // Clamp the left position so the preview doesn't go outside the canvas
      const leftClamped = Math.max(
        panelWidth / 2 + TOOLTIP_PADDING_PX,
        Math.min(left, canvasWidth - panelWidth / 2 - TOOLTIP_PADDING_PX),
      );

      // If the tooltip would overflow the bottom of the canvas, move it up above the drag and drop preview
      const topClamped =
        top > canvasHeight - panelHeight - TOOLTIP_PADDING_PX
          ? top - TOOLTIP_OFFSET_OVERFLOW_BOTTOM
          : top;

      dropPreviewRef.current?.style.setProperty(
        "transform",
        preview.snapPosition
          ? `translate(${preview.snapPosition.x}px, ${preview.snapPosition.y}px)`
          : `translate(${leftClamped}px, ${topClamped}px)`,
      );

      if (isEqual(preview, dropPreviewData)) {
        return;
      }

      setDropPreviewData(preview);
    },
    [dropPreviewData, getDropPreviewData],
  );

  const onDragLeave = useCallback(() => {
    setDropPreviewData(undefined);
  }, []);

  return (
    <DropHandler
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      canBeDropped={canBeDropped}
      enabled={canImportCAD || canImportPointCloud || canImportWaypointImages}
      draggingOverlay={<DropAreaHint />}
      appDraggingSx={{
        border: `2px dashed ${grey[500]}`,
      }}
    >
      {children}

      {dropPreviewData && (
        <DropPreview ref={dropPreviewRef} {...dropPreviewData} />
      )}
    </DropHandler>
  );
}

type WaypointNameProps = {
  /** The ID of the waypoint to display the name for. */
  waypointId: GUID;
};

function WaypointName({ waypointId }: WaypointNameProps): JSX.Element {
  const waypointElement = useAppSelector(selectIElement(waypointId));
  assert(
    !waypointElement || isIElementWaypoint(waypointElement),
    "Pano element must be defined",
  );
  const reference = useWaypointReference(waypointElement);

  return (
    <TranslateVar name="waypointName">
      {reference ? reference.name : "existing waypoint"}
    </TranslateVar>
  );
}

function DropAreaHint(): JSX.Element {
  const canImportPointCloud = useCanImportPointCloud();
  const canImportCAD = useAppSelector(selectCanUploadCad);

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        alignItems: "center",
        pt: 2,
      }}
    >
      <ContextualBanner sx={{ textAlign: "center" }}>
        <FaroText variant="bodyM" dark>
          Drag and drop your files
        </FaroText>

        <br />
        <br />

        <FaroText variant="bodyM" dark>
          Upload {canImportPointCloud ? "point clouds, " : ""}
          {canImportCAD ? "3D models, " : ""}360° photos and 2D photos
        </FaroText>
      </ContextualBanner>
    </Stack>
  );
}

type DropPreviewProps = {
  /** Whether the drop action is allowed */
  isAllowed: boolean;
  /** Whether the action should be highlighted in a different color */
  isHighlightedAction?: boolean;
  /** The position in pixels to snap the preview to. */
  snapPosition?: Vector2;
  /** The message to show in the tooltip */
  message: ReactNode;
};

const DropPreview = forwardRef(function DropPreview(
  { isAllowed, isHighlightedAction, message }: DropPreviewProps,
  ref: React.Ref<HTMLDivElement>,
): JSX.Element {
  const allowedColor = isHighlightedAction ? cyan[700] : grey[950];
  const backgroundColor = isAllowed ? allowedColor : red[500];

  return (
    <Stack
      ref={ref}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        pointerEvents: "none",
        // Initialize the preview out of view, it is moved into view via the ref on drag events.
        transform: "translateY(-100%)",
        maxWidth: "500px",
      }}
    >
      <Stack
        sx={{
          backgroundColor,
          marginTop: 5,
          padding: 1,
          borderRadius: 1,
          transform: "translateX(-50%)",
        }}
      >
        <FaroText variant="bodyM" shouldElide dark>
          {message}
        </FaroText>
      </Stack>
    </Stack>
  );
});
